import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import { t } from '../i18n'
import { Notice, Pending } from '../ui'
import { retryAfter } from './SignIn'

/**
 * Setting a password with a reset code, mailed or handed over.
 *
 * **Two codes reach this screen and it does not choose between them.** A link
 * this instance mailed carries its code in the address; a code an administrator
 * read down a phone is typed into the first field. Which of the two is in hand
 * decides whether a second factor is asked for, and that answer comes from the
 * instance rather than from anything the browser can see: the screen asks
 * `/signin/password/reset/challenge` and renders what it is told.
 *
 * **The enrolment is part of the same page on purpose.** Somebody who has never
 * set up an authenticator app is standing here holding a link and a phone, and
 * sending them away to a settings screen they cannot reach — they are not
 * signed in — would be sending them nowhere. So the QR code, the secret to type
 * by hand, and the six digits that prove the app was set up are all on this
 * page, and the password is not accepted until they are right.
 *
 * The challenge is asked for once the code is known and consumes nothing, so
 * this screen may be opened, closed while somebody finds their phone, and
 * opened again. It is asked again on every render of a new code, because the
 * code typed by hand arrives one character at a time and the answer belongs to
 * the whole of it.
 *
 * **A wait is answered as a wait**, the same as on the sign-in page. The six
 * digits behind a mailed link are guessable, so the instance counts wrong ones
 * and eventually answers 429 before it looks at anything; rendering that as a
 * refused code would send somebody whose app is perfectly right off to find a
 * new link over something that passes on its own.
 *
 * Outside the session gate, deliberately and for the same reason `/signin` is:
 * the person here is by definition the person who cannot sign in.
 */
export function ResetPassword() {
  const [params] = useSearchParams()
  const [code, setCode] = useState(() => params.get('code') ?? '')
  const [asked, setAsked] = useState('')
  /**
   * Which code a request is already out for.
   *
   * A ref rather than the state above, and the difference is the whole reason
   * it exists: state is read from the render that scheduled the effect, so two
   * renders in a row — which is what React does in development, and what a
   * re-render on any other state does at any time — both see the old value and
   * both send a request. The instance survived that and should not have had to:
   * the two requests raced to create one enrolment row and the second was
   * refused by the constraint, which reached the person holding the link as a
   * site that had fallen over.
   */
  const asking = useRef('')
  const [challenge, setChallenge] = useState<Challenge>(null)
  const [second, setSecond] = useState('')
  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')
  const [state, setState] = useState<State>(null)
  const [sending, setSending] = useState(false)

  // A code that arrived in the address is asked about as soon as the page
  // opens; one typed in is asked about when the person leaves the field. Both
  // go through the same call, and neither consumes anything.
  useEffect(() => {
    const wanted = params.get('code') ?? ''
    if (wanted) void describe(wanted)
    // `describe` is stable for the life of this component, and the ref inside
    // it is what stops a second render asking about the same code again.
  }, [params])

  // The wait counts itself down, for the reason the sign-in page's does: a
  // number that sits still reads as a page that has frozen, and somebody who
  // cannot see it shrinking goes looking for a new link they do not need.
  useEffect(() => {
    if (state?.kind !== 'waiting') return
    if (state.seconds <= 0) {
      setState(null)
      return
    }
    const timer = setTimeout(() => setState({ kind: 'waiting', seconds: state.seconds - 1 }), 1000)
    return () => clearTimeout(timer)
  }, [state])

  async function describe(wanted: string): Promise<void> {
    if (asking.current === wanted) return
    asking.current = wanted
    setAsked(wanted)
    try {
      const result = await api.POST('/signin/password/reset/challenge', {
        body: { code: wanted },
      })
      if (result.data !== undefined) {
        setChallenge(result.data)
        setState(null)
        return
      }
      setChallenge(null)
      // 503 is an instance that cannot hold a second factor at all, which is a
      // deployment problem and not this person's code.
      setState({ kind: result.response.status === 503 ? 'unavailable' : 'code' })
    } catch {
      setChallenge(null)
      setState({ kind: 'unreachable' })
    }
  }

  async function submit(): Promise<void> {
    setSending(true)
    try {
      const result = await api.POST('/signin/password/reset', {
        body: { code, password, second_factor_code: second },
      })
      if (result.response.status === 204) {
        setState({ kind: 'done' })
        return
      }
      if (result.response.status === 422) {
        setState({ kind: 'password' })
        return
      }
      // The instance has not looked at the six digits at all, so this is the
      // one refusal on this screen that is not about anything that was typed.
      // It has to read as a wait: somebody whose app is right, told instead
      // that their code was not accepted, goes looking for a new link or for
      // an administrator over something that clears itself in five minutes.
      if (result.response.status === 429) {
        setState({ kind: 'waiting', seconds: retryAfter(result.response) })
        return
      }
      const key =
        (result.error as { detail?: { message_key?: string } } | undefined)?.detail
          ?.message_key ?? ''
      if (key === 'signin.error.second_factor_refused') {
        setState({ kind: 'second' })
        return
      }
      if (key === 'signin.error.second_factor_required') {
        setState({ kind: 'second_missing' })
        return
      }
      if (key === 'signin.error.second_factor_unavailable') {
        setState({ kind: 'unavailable' })
        return
      }
      // Everything else is the code, and is one answer: expired, already used,
      // or never issued are three things an attacker would like told apart.
      setState({ kind: 'code' })
    } catch {
      setState({ kind: 'unreachable' })
    } finally {
      setSending(false)
    }
  }

  if (state?.kind === 'done') {
    return (
      <div className="signin">
        <h2 className="page">{t('app.name')}</h2>
        <Notice tone="good">{t('reset.done')}</Notice>
        <p className="muted">
          <Link to="/signin">{t('reset.now_sign_in')}</Link>
        </p>
      </div>
    )
  }

  const enrolment = challenge?.enrolment ?? null
  const wantsSecond = challenge?.second_factor_required ?? false
  const waiting = state?.kind === 'waiting' ? state.seconds : 0

  return (
    <div className="signin">
      <h2 className="page">{t('app.name')}</h2>
      <p className="page-sub">{t('reset.title')}</p>

      {state?.kind === 'code' ? <Notice tone="bad">{t('reset.code_refused')}</Notice> : null}
      {state?.kind === 'password' ? (
        <Notice tone="bad">{t('reset.password_shape')}</Notice>
      ) : null}
      {state?.kind === 'second' ? <Notice tone="bad">{t('reset.second_refused')}</Notice> : null}
      {state?.kind === 'second_missing' ? (
        <Notice tone="warn">{t('reset.second_required')}</Notice>
      ) : null}
      {state?.kind === 'unavailable' ? (
        <Notice tone="bad">{t('reset.second_unavailable')}</Notice>
      ) : null}
      {state?.kind === 'mismatch' ? <Notice tone="warn">{t('reset.mismatch')}</Notice> : null}
      {state?.kind === 'unreachable' ? (
        <Notice tone="bad">{t('signin.unreachable')}</Notice>
      ) : null}
      {waiting > 0 ? (
        <Notice tone="warn">{t('signin.waiting').replace('{seconds}', String(waiting))}</Notice>
      ) : null}

      <form
        className="card"
        onSubmit={(event) => {
          event.preventDefault()
          if (sending || waiting > 0) return
          if (password !== again) {
            // Checked here rather than at the API, because it is the one thing
            // the browser can know: the server is never sent two passwords and
            // has no opinion about whether they matched.
            setState({ kind: 'mismatch' })
            return
          }
          setState(null)
          void submit()
        }}
      >
        <h3>{t('reset.heading')}</h3>
        <p className="muted">{t('reset.explain')}</p>
        <div className="field">
          <label htmlFor="code">{t('reset.code')}</label>
          <input
            id="code"
            name="code"
            className="mono"
            autoComplete="off"
            spellCheck={false}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            onBlur={() => {
              if (code) void describe(code)
            }}
          />
        </div>

        {code && code === asked && challenge === null && state === null ? <Pending /> : null}

        {enrolment ? (
          <div className="field">
            <h4>{t('reset.enrol_heading')}</h4>
            <p className="muted">{t('reset.enrol_explain')}</p>
            {/* The instance's own SVG, built from the enrolment address by
                `sitefocus.core.totp.qr_svg` and put straight into the page.
                Inline rather than an image URL on purpose: a QR code fetched
                from an address is a secret somebody else can ask for. */}
            <div
              className="qr"
              data-testid="second-factor-qr"
              dangerouslySetInnerHTML={{ __html: enrolment.qr_svg }}
            />
            <p className="muted">{t('reset.enrol_by_hand')}</p>
            <p className="mono" data-testid="second-factor-secret">
              {enrolment.secret}
            </p>
          </div>
        ) : null}

        {wantsSecond ? (
          <div className="field">
            <label htmlFor="second-factor">{t('reset.second_factor')}</label>
            <input
              id="second-factor"
              name="second-factor"
              className="mono"
              inputMode="numeric"
              autoComplete="one-time-code"
              spellCheck={false}
              value={second}
              onChange={(event) => setSecond(event.target.value)}
            />
          </div>
        ) : null}

        <div className="field">
          <label htmlFor="new-password">{t('reset.new_password')}</label>
          <input
            id="new-password"
            name="new-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="again">{t('reset.again')}</label>
          <input
            id="again"
            name="again"
            type="password"
            autoComplete="new-password"
            value={again}
            onChange={(event) => setAgain(event.target.value)}
          />
        </div>
        <button type="submit" className="primary" disabled={sending || waiting > 0}>
          {t('reset.submit')}
        </button>
        <p className="muted">
          <Link to="/signin/forgot">{t('reset.ask_for_a_link')}</Link>
        </p>
        <p className="muted">
          <Link to="/signin">{t('reset.back')}</Link>
        </p>
      </form>
    </div>
  )
}

type Challenge = {
  second_factor_required: boolean
  enrolled: boolean
  enrolment?: { secret: string; uri: string; qr_svg: string } | null
} | null

type State =
  | { kind: 'code' }
  /** Counted down rather than sat still, exactly as the sign-in page's is. */
  | { kind: 'waiting'; seconds: number }
  | { kind: 'password' }
  | { kind: 'second' }
  | { kind: 'second_missing' }
  | { kind: 'unavailable' }
  | { kind: 'mismatch' }
  | { kind: 'unreachable' }
  | { kind: 'done' }
  | null
